import { checkTwoDeviceCanConnect, getDeviceTypeLevels } from "@/commons/enums/model-type";
import { AddPlineCommand, BasicControl, BlockT, CURSOR_TYPE, DYMODE_MODE, FatLine, LineSegments2, LineT, Listeners, MOUSE, ModelBase, PlineAttributes, Point, PrimitiveColor, TnEngineContext, TnEngineExtraContext, ViewEditor, dymode } from "pytha";
import { BufferGeometry, CatmullRomCurve3, Color, Line, LineBasicMaterial, LineDashedMaterial, LineSegments, Plane, Vector3 } from "three";
import { ARCHI_CTRL_TYPE } from "../enums/archibase-control-type";
import { ARCHIBASE_SINGAL } from "../enums/archibase-singal";
import { RoamPointT } from "../models/RoamPointT";


export class DrawRoamRouteControl extends BasicControl {
    id = ARCHI_CTRL_TYPE.DRAW_SECTION;

    point_down: Vector3 = new Vector3();
    point_move: Vector3 = new Vector3();

    // helpCurve: CatmullRomCurve3 = new CatmullRomCurve3();
    helpPointArr: Vector3[] = [];
    helpCurveLine: Line;

    orthogonalPoint: Vector3;
    capturePoint: Vector3;

    dymodeChoose: string = null;
    next: boolean = false; // 当前正在输入端点的属性值
    vertexIdx: number = 0; // 当前顶点

    _startToCenter: Vector3 = new Vector3();
    _startToEnd: Vector3 = new Vector3();
    _tangentDir: Vector3 = new Vector3();
    _tangentDir_UCS: Vector3 = new Vector3();

    needInit: boolean;
    roamRouteUuid: string;
    layerUuid: string;

    options = [
        { name: '绘制结束', key: 'C' },
        { name: '放弃', key: 'U' },
    ]
    polarPoint: Vector3;
    helpRayLine: LineT;
    normal: Vector3;
    origin: Vector3;
    axisX: Vector3;
    axisY: Vector3;
    axisZ: Vector3;

    step: number = 0;

    context: TnEngineContext;
    extraContext: TnEngineExtraContext;

    constructor(editor: ViewEditor, listeners: Listeners) {
        super();
        /** 初始化 */
        this.needInit = true;
        this.editor = editor;
        this.listeners = listeners;
        this.context = TnEngineContext.getInstance();
        this.extraContext = TnEngineExtraContext.getInstance();
        this.normal = this.context.ucsContext.getAxisList().axisZ.clone();
        this.origin = this.context.ucsContext.getAxisList().origin.clone();
        this.axisX = this.context.ucsContext.getAxisList().axisX.clone();
        this.axisY = this.context.ucsContext.getAxisList().axisY.clone();
        this.axisZ = this.context.ucsContext.getAxisList().axisZ.clone();
        this._tangentDir = this.context.ucsContext.getAxisList().axisX.clone();
        editor.renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
        editor.renderer.domElement.addEventListener('pointermove', this.onPointerMove);
        /** 注册一些信号 */
        this.listeners.signals.needCapture.dispatch();
        this.listeners.signals.emptySelectedEntities.dispatch();
        this.listeners.signals.onPointCapture.add(this.onPointCapture);
        this.listeners.signals.onDymodeChanged.add(this.onDymodeChanged);
        this.listeners.signals.onKeyMouseConfirm.add(this.onKeyMouseConfirm);
        this.listeners.signals.onPointOrthogonal.add(this.onPointOrthogonal);
        this.listeners.signals.onPointPolarTracking.add(this.onPointPolarTracking);
        this.listeners.signals.onZoomChanged.add(this.onZoomChanged);
        this.listeners.getSignal(ARCHIBASE_SINGAL.drawRoamRoute).add(this.drawRoamRoute);

        this.step = 1;
        this.editor.selectControl.setEnabled(false);
        this.extraContext.dymodeContext.enabled = true;
        this.extraContext.dymodeContext.setMode(DYMODE_MODE.POINT);
        this.point_down.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        this.point_move.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        /** 清空选择 */
        this.editor.selectControl.resetSelectedEntities();
        /** 这里：提示选择路径点 */
        this.listeners.signals.sendToDymodeTip.dispatch('选择路径起点:');
        this.extraContext.stmodeContext.setStmode('选择路径起点');
        this.listeners.signals.onCursorChange.dispatch(CURSOR_TYPE.CROSS);
        console.log('DrawRoamRouteControl added')
    }

    // 二级控制器销毁
    dispose(): void {
        // 删除辅助线
        if (this.helpCurveLine && this.helpCurveLine.parent) {
            this.helpCurveLine.parent.remove(this.helpCurveLine);
            this.helpCurveLine = null;
        }
        if (this.helpRayLine?.viewObj && !!this.helpRayLine?.viewObj.parent) {
            this.helpRayLine?.viewObj.parent.remove(this.helpRayLine?.viewObj);
            this.helpRayLine = null;
        }
        this.editor.renderer.domElement.removeEventListener('pointerdown', this.onPointerDown);
        this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
        this.listeners.signals.onPointCapture.remove(this.onPointCapture);
        this.listeners.signals.onKeyMouseConfirm.remove(this.onKeyMouseConfirm);
        this.listeners.signals.onPointOrthogonal.remove(this.onPointOrthogonal);
        this.listeners.signals.onDymodeChanged.remove(this.onDymodeChanged);
        this.listeners.signals.onPointPolarTracking.remove(this.onPointPolarTracking);
        this.listeners.signals.onZoomChanged.remove(this.onZoomChanged);
        this.listeners.getSignal(ARCHIBASE_SINGAL.drawRoamRoute).remove(this.drawRoamRoute);
        this.listeners.signals.noNeedCapture.dispatch();
        this.editor.selectControl.setEnabled(true);
        this.extraContext.dymodeContext.enabled = false;
        this.editor.orthogonalControl.enabled = false;
        console.log('DrawRoamRouteControl removed')
    }

    drawRoamRoute = (roamRouteUuid: string, layerUuid: string) => {
        this.layerUuid = layerUuid;
        this.roamRouteUuid = roamRouteUuid;
        let initData = {
            layerUuid: layerUuid,
            roamRouteUuid: roamRouteUuid,
        }
        this.listeners.signals.setInitControlData.dispatch(initData);
    }

    // 初始化极轴的射线
    initHelpRayLine = () => {
        this.helpRayLine = new LineT();
        this.helpRayLine._isHelpLine = true;
        let viewObj: LineSegments | FatLine | LineSegments2 = this.helpRayLine.render();
        let size = this.context.renderContext.cameraEdge * this.context.renderContext.selectedDashScale * 5;
        viewObj.material = new LineDashedMaterial({ color: new Color('#00a500'), dashSize: size, gapSize: size });
    }

    updateHelpLine = (v: Vector3) => {
        // 有超过两个点才能创建曲线
        if (this.helpPointArr?.length < 1) return;
        // 如果已经有辅助线存在，先移除旧的线条对象
        if (this.helpCurveLine && this.helpCurveLine.parent) {
            this.helpCurveLine.parent.remove(this.helpCurveLine);
        }
        // 创建新的曲线对象
        let pointArr = [...this.helpPointArr];
        pointArr.push(v);
        const curve = new CatmullRomCurve3(pointArr);
        const geometry = new BufferGeometry().setFromPoints(curve.getPoints(100));
        const material = new LineBasicMaterial({ color: 0x072983 });
        const curveObject = new Line(geometry, material);
        this.extraContext.sceneContext.scene.add(curveObject);
        this.helpCurveLine = curveObject;
    }

    onZoomChanged = (zoomScale) => {
        if (!this.helpRayLine?.viewObj) return;
        let size = this.context.renderContext.cameraEdge * this.context.renderContext.selectedDashScale * 5;
        let mat = <LineDashedMaterial>this.helpRayLine.viewObj.material;
        mat.dashSize = size;
        mat.gapSize = size;
    }

    onPointPolarTracking = (point) => {
        this.polarPoint = point;
        if (!point) {
            this.extraContext.sceneContext.scene.remove(this.helpRayLine?.viewObj);
        }
    }

    onPointCapture = (point) => {
        this.capturePoint = point;
    }

    onPointOrthogonal = (point) => {
        this.orthogonalPoint = point;
    }

    private _lastPointDown: Vector3 = new Vector3();
    private _currentPointDown: Vector3 = new Vector3();

    onPointerDown = (event) => {
        if (event.button !== MOUSE.LEFT) return;
        this._lastPointDown.copy(this.point_down.clone());

        let rect = this.editor.renderer.domElement.getBoundingClientRect();
        let x = (event.clientX - rect.x) / rect.width * 2 - 1;
        let y = -(event.clientY - rect.y) / rect.height * 2 + 1;
        this.context.ucsContext.unprojectCursorPoint(x, y, this.editor.camera, this.point_down);
        this._currentPointDown.copy(this.point_down.clone());
        if (this.orthogonalPoint) {
            this.point_down.copy(this.orthogonalPoint);
        }
        if (this.capturePoint) {
            this.point_down.copy(this.capturePoint);
        }
        if (this.polarPoint) {
            this.point_down.copy(this.polarPoint);
        }

        // 两个点相同，不创建
        if (this._lastPointDown.equals(this.point_down)) return;
        this.step_4(this.point_down);
    }

    onPointerMove = (event) => {
        // 第四步才布置点
        this.point_move.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);

        if (this.orthogonalPoint) {
            this.point_move.copy(this.orthogonalPoint);
        }
        if (this.capturePoint) {
            this.point_move.copy(this.capturePoint);
        }

        if (this.point_move.equals(this.point_down)) return;
        this.updateHelpLine(this.point_move);
    }


    // 不停的点选下一个点 下一个点
    step_4 = (v: Vector3) => {
        this.helpPointArr.push(v.clone());
        this.listeners.signals.sendToDymodeTip.dispatch('指定下一个点:');
        this.extraContext.stmodeContext.setStmode('指定下一个点或', this.options);
        this.listeners.signals.needCapture.dispatch(this.point_down);
        this.listeners.signals.orthogonal.dispatch(this.point_down);
        this.listeners.signals.polarTracking.dispatch(this.point_down);
    }

    _dymodeActive = false;

    onDymodeChanged = (value: dymode) => {
        if (value.mode === DYMODE_MODE.POINT) {
            this.point_down.set(value.x, value.y, value.z);
            this._dymodeActive = true;
            this.onKeyMouseConfirm();
        } else if (value.mode === DYMODE_MODE.CHOOSE) {
            let choose = value.choose.toUpperCase();
            this.dymodeChoose = choose;
            switch (choose) {
                case 'U':
                    // 放弃
                    this.onGiveUp();
                    break;
                case 'C':
                    this.onKeyMouseConfirm(false);
                    break;
            }
        }
    }

    // 按下回车/左键的回调
    onKeyMouseConfirm = (isRightClick = true) => {
        if (isRightClick) {
            this.onGiveUp();
        } else {
            this.onCreateFinish();
        }
    }

    // 按下放弃的回调
    onGiveUp = () => {
        this.editor.listeners.signals.onOpeCommandControlEnd.dispatch();
    }

    // 发送结束布置的信号，并结束二级控制器
    onCreateFinish = () => {
        if (this.helpPointArr?.length < 1) {
            this.listeners.signals.onOpeCommandControlEnd.dispatch();
            return;
        }
        let roamPointArr: RoamPointT[] = [];
        for (let item of this.helpPointArr) {
            let roamPoint = new RoamPointT(new Point(item.x, item.y, item.z));
            roamPoint.layerUuid = this.layerUuid;
            roamPointArr.push(roamPoint);
        }
        this.editor.listeners.getSignal(ARCHIBASE_SINGAL.drawRoamRouteFinish).dispatch(this.roamRouteUuid, roamPointArr);
        this.listeners.signals.onOpeCommandControlEnd.dispatch();
    }

    initControl(data: any): void {
        if (!!data) {
            this.drawRoamRoute(data.roamRouteUuid, data.layerUuid);
        }
    }
}